import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faStar, faFilm, faTv } from '@fortawesome/free-solid-svg-icons';
import type { MediaItem } from '../../types';
import { getPosterUrl, getMediaTitle, getMediaYear } from '../../utils';


interface MediaCardProps {
    item: MediaItem;
    index?: number;
    size?: 'small' | 'medium' | 'large' | 'full';
    showRating?: boolean;
    showType?: boolean;
    className?: string;
}

const MediaCard: React.FC<MediaCardProps> = ({
    item,
    index = 0,
    size = 'medium',
    showRating = true,
    showType = false,
    className = ''
}) => {
    const navigate = useNavigate();
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    const mediaType = item.media_type;
    const title = getMediaTitle(item);
    const year = getMediaYear(item);

    const handleClick = () => {
        navigate(`/media/${mediaType}/${item.id}`);
    };

    const sizeClasses = {
        small: 'w-28 sm:w-32',
        medium: 'w-36 sm:w-40 md:w-44 lg:w-48',
        large: 'w-44 sm:w-52 md:w-60 lg:w-72',
        full: 'w-full',
    };

    const posterUrl = getPosterUrl(item.poster_path, size === 'small' ? 'medium' : 'large');

    return (
        <div
            onClick={handleClick}
            className={`
        group relative cursor-pointer
        ${sizeClasses[size]}
        ${className}
      `}
            style={{
                animationDelay: `${index * 50}ms`,
            }}
        >
            {/* Card Container */}
            <div className="relative aspect-[2/3] rounded-xl overflow-hidden 
        bg-gradient-to-br from-gray-800 to-gray-900
        shadow-lg shadow-black/30
        transform transition-all duration-500 ease-out
        group-hover:scale-105 group-hover:shadow-2xl group-hover:shadow-amber-500/20
        group-focus-within:scale-105 group-focus-within:shadow-2xl
        ring-2 ring-transparent group-hover:ring-amber-500/50
      ">

                {/* Skeleton Loading */}
                {!imageLoaded && !imageError && (
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-800 animate-pulse">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent skeleton-shimmer" />
                    </div>
                )}

                {/* Poster Image */}
                <img
                    src={posterUrl || '/placeholder-poster.jpg'}
                    alt={title}
                    className={`
            w-full h-full object-cover
            transition-all duration-700
            ${imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-105'}
            group-hover:scale-110
          `}
                    loading="lazy"
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageError(true)}
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent 
          opacity-60 group-hover:opacity-80 transition-opacity duration-300" />

                {/* Play Button */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 
          group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-100 scale-75">
                    <div className="p-4 sm:p-5 rounded-full bg-amber-500/90 backdrop-blur-sm
            shadow-2xl shadow-amber-500/50
            transform transition-all duration-300
            group-hover:scale-110 group-hover:bg-amber-500">
                        <FontAwesomeIcon icon={faPlay} className="text-white text-xl sm:text-2xl ml-1" />
                    </div>
                </div>

                {/* Rating Badge */}
                {showRating && item.vote_average > 0 && (
                    <div className="absolute top-2 left-2 sm:top-3 sm:left-3
            flex items-center gap-1 px-2 py-1 rounded-lg
            bg-black/70 backdrop-blur-sm
            text-xs sm:text-sm font-semibold">
                        <FontAwesomeIcon icon={faStar} className="text-amber-400" />
                        <span className="text-white">{item.vote_average.toFixed(1)}</span>
                    </div>
                )}

                {/* Media Type Badge */}
                {showType && (
                    <div className="absolute top-2 right-2 sm:top-3 sm:right-3
            p-1.5 sm:p-2 rounded-lg
            bg-black/70 backdrop-blur-sm">
                        <FontAwesomeIcon
                            icon={mediaType === 'movie' ? faFilm : faTv}
                            className={`text-sm ${mediaType === 'movie' ? 'text-rose-400' : 'text-cyan-400'}`}
                        />
                    </div>
                )}

                {/* Title Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4
          transform translate-y-full group-hover:translate-y-0
          transition-transform duration-300 ease-out">
                    <h3 className="text-white font-bold text-sm sm:text-base line-clamp-2 drop-shadow-lg">
                        {title}
                    </h3>
                    {year && (
                        <p className="text-gray-300 text-xs sm:text-sm mt-1">{year}</p>
                    )}
                </div>
            </div>

            {/* Focus indicator for accessibility */}
            <button
                className="absolute inset-0 rounded-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-500/50"
                aria-label={`Watch ${title}`}
            />
        </div>
    );
};

export default MediaCard;
